import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import {
  buildTimeline,
  fetchTraceText,
  parseTraceEvents,
  type ThinkingBlock,
  type TextBlock,
  type ToolBlock,
  type TurnItem,
  type UnknownBlock,
} from '../trace';

const LONG_RESULT = 2000;

function Json({ value }: { value: unknown }) {
  return <pre className="tl-pre">{JSON.stringify(value, null, 2)}</pre>;
}

/** A block of text (thinking or assistant message). Full text, never truncated; collapsible, default open. */
function ProseBlock({ label, text, tone }: { label: string; text: string; tone: 'thinking' | 'text' }) {
  return (
    <details className={`tl-block tl-${tone}`} open>
      <summary>
        <span className="label">{label}</span>
        <span className="faint mono">{text.length.toLocaleString()} chars</span>
      </summary>
      <div className={tone === 'thinking' ? 'tl-thinking-body' : 'tl-text-body'}>{text || '(empty)'}</div>
    </details>
  );
}

function ToolResultBody({ text }: { text: string }) {
  const long = text.length > LONG_RESULT;
  return <pre className={long ? 'tl-pre tl-pre-scroll' : 'tl-pre'}>{text || '(empty result)'}</pre>;
}

function durationLabel(ms: number | undefined): string | null {
  if (ms === undefined || !Number.isFinite(ms) || ms < 0) return null;
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`;
}

function ToolBlockView({ block }: { block: ToolBlock }) {
  const dur = durationLabel(block.durationMs);
  return (
    <details className={`tl-block tl-tool${block.result?.isError ? ' tl-tool-error' : ''}`} open>
      <summary>
        <span className="label">tool call</span>
        <span className="mono tl-tool-name">{block.name}</span>
        {!block.complete ? <span className="chip">incomplete</span> : null}
        {block.result?.isError ? <span className="chip bad">error</span> : null}
        {dur ? (
          <span className="faint mono" style={{ marginLeft: 'auto' }}>
            {dur}
          </span>
        ) : null}
      </summary>
      <div className="tl-tool-body">
        <div className="label" style={{ margin: '10px 0 4px' }}>
          args
        </div>
        <Json value={block.args} />
        <div className="label" style={{ margin: '12px 0 4px' }}>
          result{block.result?.isError ? ' (error)' : ''}
        </div>
        {block.result ? (
          <ToolResultBody text={block.result.text} />
        ) : (
          <p className="faint" style={{ margin: 0 }}>
            no result recorded (run may have been aborted before this call finished)
          </p>
        )}
      </div>
    </details>
  );
}

function UnknownBlockView({ block }: { block: UnknownBlock }) {
  return (
    <details className="tl-block tl-unknown">
      <summary>
        <span className="label">unrecognized content</span>
        <span className="mono">{block.contentType}</span>
      </summary>
      <Json value={block.raw} />
    </details>
  );
}

function fmtTime(ts: string | undefined): string {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString();
}

function TurnView({ turn }: { turn: TurnItem }) {
  const durMs =
    turn.startTs && turn.endTs ? new Date(turn.endTs).getTime() - new Date(turn.startTs).getTime() : undefined;
  return (
    <section className="tl-turn">
      <header className="tl-turn-head">
        <span className="tl-turn-badge">Turn {turn.turnIndex + 1}</span>
        {!turn.complete ? <span className="chip bad">incomplete — turn aborted</span> : null}
        <span className="faint mono">{fmtTime(turn.startTs)}</span>
        {durationLabel(durMs) ? <span className="faint mono">{durationLabel(durMs)}</span> : null}
        {turn.usage ? (
          <span className="faint mono" style={{ marginLeft: 'auto' }}>
            {turn.usage.totalTokens.toLocaleString()} tok · est. ${turn.usage.costTotal.toFixed(4)}
          </span>
        ) : null}
      </header>
      <div className="tl-turn-blocks">
        {turn.blocks.length === 0 ? <p className="faint" style={{ margin: 0 }}>(no content this turn)</p> : null}
        {turn.blocks.map((b, i) => {
          if (b.kind === 'thinking') return <ProseBlock key={i} label="thinking" text={(b as ThinkingBlock).text} tone="thinking" />;
          if (b.kind === 'text') return <ProseBlock key={i} label="assistant" text={(b as TextBlock).text} tone="text" />;
          if (b.kind === 'tool') return <ToolBlockView key={i} block={b as ToolBlock} />;
          return <UnknownBlockView key={i} block={b as UnknownBlock} />;
        })}
      </div>
    </section>
  );
}

/** Renders a full reconstructed trace timeline from raw JSONL text. Used inline in the Activity accordion and on the ActivityDetail page. */
export function TraceTimeline({ text }: { text: string }) {
  const timeline = useMemo(() => buildTimeline(parseTraceEvents(text)), [text]);

  if (timeline.length === 0) {
    return <div className="empty">Awaiting data — the trace is empty.</div>;
  }

  return (
    <div className="tl">
      {timeline.map((item, i) => {
        switch (item.kind) {
          case 'run_start':
            return (
              <details className="tl-block tl-run-start" key={i}>
                <summary>
                  <span className="label">run start</span>
                  <span className="mono">{item.model}</span>
                  {item.label ? <span className="muted">{item.label}</span> : null}
                  {item.maxTurns ? <span className="faint mono">max {item.maxTurns} turns</span> : null}
                </summary>
                <div className="tl-tool-body">
                  {item.tools?.length ? (
                    <>
                      <div className="label" style={{ margin: '10px 0 4px' }}>
                        tools
                      </div>
                      <div className="tl-chip-row">
                        {item.tools.map((t) => (
                          <span className="chip" key={t}>
                            {t}
                          </span>
                        ))}
                      </div>
                    </>
                  ) : null}
                  {item.systemPrompt ? (
                    <>
                      <div className="label" style={{ margin: '12px 0 4px' }}>
                        system prompt
                      </div>
                      <pre className="tl-pre tl-pre-scroll">{item.systemPrompt}</pre>
                    </>
                  ) : null}
                  {item.userPrompt ? (
                    <>
                      <div className="label" style={{ margin: '12px 0 4px' }}>
                        user prompt
                      </div>
                      <pre className="tl-pre tl-pre-scroll">{item.userPrompt}</pre>
                    </>
                  ) : null}
                </div>
              </details>
            );
          case 'turn':
            return <TurnView key={i} turn={item} />;
          case 'run_end':
            return (
              <details className="tl-block tl-run-end" key={i} open>
                <summary>
                  <span className="label">run end</span>
                  <span className="mono">
                    {item.turns ?? 0} turns · {item.toolCalls ?? 0} tool calls
                  </span>
                  {item.aborted ? <span className="chip bad">aborted</span> : null}
                  {item.cost !== undefined ? (
                    <span className="faint mono" style={{ marginLeft: 'auto' }}>
                      est. ${item.cost.toFixed(4)}
                    </span>
                  ) : null}
                </summary>
                <div className="tl-tool-body">
                  {item.finalText ? (
                    <>
                      <div className="label" style={{ margin: '10px 0 4px' }}>
                        final assistant text
                      </div>
                      <div className="tl-text-body">{item.finalText}</div>
                    </>
                  ) : null}
                  {item.tokens ? (
                    <>
                      <div className="label" style={{ margin: '12px 0 4px' }}>
                        tokens
                      </div>
                      <Json value={item.tokens} />
                    </>
                  ) : null}
                </div>
              </details>
            );
          case 'run_error':
            return (
              <details className="tl-block tl-run-error" key={i} open>
                <summary>
                  <span className="label fail">run error</span>
                </summary>
                <pre className="tl-pre fail">{item.error}</pre>
              </details>
            );
          case 'raw':
            return (
              <details className="tl-block tl-raw" key={i}>
                <summary>
                  <span className="label">{item.type}</span>
                  <span className="faint mono">{fmtTime(item.ts)}</span>
                </summary>
                <Json value={item.raw} />
              </details>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

/** Fetches (and caches) a run's trace text on mount, then renders the full timeline. Mount this only once the caller wants to load — i.e. on first expand. */
export function TraceLoader({ runId }: { runId: number }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);
    fetchTraceText(runId, (path) => api.text(path))
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (error) return <div className="empty">Awaiting data — no trace for this run ({error}).</div>;
  if (text === null) return <div className="empty">Loading trace…</div>;
  return <TraceTimeline text={text} />;
}
